import React, { useEffect, useRef, useState } from "react";
import "./messages.css";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { MdOutlinePushPin } from "react-icons/md";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { UilAngleLeftB, UilAngleRightB } from "@iconscout/react-unicons";

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [images] = useState([
    "/photos/chat-1.svg",
    "/photos/chat-2.svg",
    "/photos/chat-3.svg",
  ]);
  const [randomImage, setRandomImage] = useState("");
  const [pinnedMessages, setPinnedMessages] = useState(
    JSON.parse(localStorage.getItem("pinnedMessages")) || []
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [lang] = useState(localStorage.getItem("lang") || `"ar"`);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);
  const [bundleLoading, setBundleLoading] = useState(false);

  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/messages?page=${currentPage}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setMessages(response?.data?.messages);
        setTotalPages(response?.data?.totalPages);
        setTotalMessages(response?.data?.totalMessages);
        localStorage.setItem(
          "messages",
          JSON.stringify(response?.data?.messages)
        );
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    };

    const getRandomImage = () => {
      const randomIndex = Math.floor(Math.random() * images.length);
      return images[randomIndex];
    };

    fetchMessages();
    setRandomImage(getRandomImage());
  }, [images, currentPage, token]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handlePinMessage = (messageId) => {
    let updatedPinnedMessages;
    if (pinnedMessages.includes(messageId)) {
      updatedPinnedMessages = pinnedMessages.filter((id) => id !== messageId);
    } else {
      updatedPinnedMessages = [messageId, ...pinnedMessages];
    }
    setPinnedMessages(updatedPinnedMessages);
    localStorage.setItem(
      "pinnedMessages",
      JSON.stringify(updatedPinnedMessages)
    );
    setShowMenu(false);
  };

  const handleToggleReadMessage = async (message) => {
    try {
      const updatedIsRead = !message?.isRead;

      await axios.put(
        `/messages/read-message/${message?._id}`,
        {
          isRead: updatedIsRead,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg._id === message._id ? { ...msg, isRead: updatedIsRead } : msg
        )
      );

      setShowMenu(false);
      toast.success("The message has been read..!");
    } catch (error) {
      toast.error("Failed to update the message status.");
    }
  };

  const confirmDelete = (messageId) => {
    setShowMenu(false);
    setSelectedMessageId(messageId);
    setShowConfirmation(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/messages/${selectedMessageId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessages(
        messages.filter((message) => message._id !== selectedMessageId)
      );
      setPinnedMessages(
        pinnedMessages.filter((id) => id !== selectedMessageId)
      );
      localStorage.setItem(
        "pinnedMessages",
        JSON.stringify(pinnedMessages.filter((id) => id !== selectedMessageId))
      );
      setShowConfirmation(false);
      setSelectedMessageId(null);
      toast.success("The message has been deleted..!");
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const handleMenuClick = (event, messageId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setShowMenu(showMenu === messageId ? null : messageId);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDownloadBundle = async () => {
    try {
      setBundleLoading(true);
      const res = await axios.get("/content/join-contact/bundle", {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "join-contact-backup.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Backup download failed.");
    } finally {
      setBundleLoading(false);
    }
  };

  const handleImportSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBundleLoading(true);
      const form = new FormData();
      form.append("bundle", file);
      await axios.post("/content/join-contact/import", form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Backup imported.");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.message || "Import failed.");
    } finally {
      setBundleLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sortedMessages = [
    ...messages.filter((message) => pinnedMessages.includes(message._id)),
    ...messages
      .filter((message) => !pinnedMessages.includes(message._id))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
  ];

  if (loading) {
    return (
      <div className="loader-container">
        <BeatLoader />
      </div>
    );
  }

  return (
    <div className="messages-page">
      <div className="container">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0 }}>Messages {`(${totalMessages})`}</h1>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" onClick={handleDownloadBundle} disabled={bundleLoading}>
              {bundleLoading ? "Working…" : "Download backup (Join Us + Contact)"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleImportSelected}
              disabled={bundleLoading}
            />
          </div>
        </div>
        <div className="boxes">
          {sortedMessages?.map((message) => (
            <div
              key={message._id}
              className={`box ${
                pinnedMessages.includes(message._id) ? "pinned" : ""
              }`}
            >
              <div className="box-header">
                <i
                  className="uil uil-ellipsis-v"
                  onClick={(event) => handleMenuClick(event, message._id)}
                ></i>

                {showMenu === message._id && (
                  <div
                    className="menu"
                    ref={menuRef}
                    style={{
                      position: "fixed",
                      top: `${menuPosition.top}px`,
                      left: `${menuPosition.left}px`,
                    }}
                  >
                    <button onClick={() => handlePinMessage(message._id)}>
                      {pinnedMessages.includes(message._id) ? "Unpin" : "Pin"}{" "}
                      Message
                    </button>
                    <button onClick={() => handleToggleReadMessage(message)}>
                      Mark as {message?.isRead === true ? "unread" : "read"}
                    </button>
                    <button onClick={() => confirmDelete(message._id)}>
                      Delete
                    </button>
                  </div>
                )}
                <div className="actions-icons">
                  {message?.isRead === true && (
                    <i
                      className="uil uil-eye read-icon"
                      onClick={() => handleToggleReadMessage(message)}
                    ></i>
                  )}
                  <MdOutlinePushPin
                    className={`pin ${
                      pinnedMessages.includes(message._id) ? "active" : ""
                    }`}
                    onClick={() => handlePinMessage(message._id)}
                  />
                  <i className="uil uil-info-circle" id="message-datee"></i>
                  <Tooltip
                    anchorSelect="#message-datee"
                    content={`${message?.createdAt?.split("T")[0]}`}
                    place="top"
                  />
                </div>
              </div>
              <div className="box-content">
                <div className="box-image">
                  <img src={randomImage} alt="message" />
                </div>
                <h3>{message?.name}</h3>
                <div className="message-data">
                  <div className="email">
                    <p>Email:</p>
                    <p>{message?.email}</p>
                  </div>
                  <div className="phone">
                    <p>Phone:</p>
                    <p>{message?.phone}</p>
                  </div>
                </div>
                <hr />
                <p className="message">{message?.message}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="pagination" dir="ltr">
          {currentPage >= 1 && (
            <div
              onClick={() => handlePageChange(currentPage - 1)}
              className="active"
            >
              {lang === "en" ? (
                <UilAngleRightB size={32} color="white" />
              ) : (
                <UilAngleLeftB size={32} color="white" />
              )}
            </div>
          )}

          {Array.from({ length: totalPages }, (_, index) => index + 1)
            .filter((pageNumber) => {
              if (pageNumber === 1 || pageNumber === totalPages) return true;
              if (
                pageNumber >= currentPage - 1 &&
                pageNumber <= currentPage + 1
              ) {
                return true;
              }
              return false;
            })
            .map((pageNumber, index, filteredPages) => (
              <>
                {index > 0 && pageNumber !== filteredPages[index - 1] + 1 && (
                  <span className="dots">...</span>
                )}
                <div
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={currentPage === pageNumber ? "active" : ""}
                >
                  {pageNumber}
                </div>
              </>
            ))}

          {currentPage < totalPages && (
            <div
              onClick={() => handlePageChange(currentPage + 1)}
              className="active"
            >
              {lang === `"ar"` ? (
                <UilAngleRightB size={32} color="white" />
              ) : (
                <UilAngleLeftB size={32} color="white" />
              )}
            </div>
          )}
        </div>
      </div>
      {showConfirmation && (
        <div className="confirmation-modal">
          <div className="modal-content sec">
            <img src="/photos/question.svg" alt="?" />
            <p>Are you sure you want to delete this message?</p>
            <div className="modal-actions">
              <button onClick={handleDelete}>Yes</button>
              <button onClick={() => setShowConfirmation(false)}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
